<template>
  <div class="rank-badge" :class="{ placement: isPlacing }" :title="label">
    <img
      v-if="src"
      :src="src"
      alt=""
      class="rank-medal"
      :width="size"
      :height="size"
    />
    <span v-else class="rank-empty" :style="{ width: size + 'px', height: size + 'px' }" />
    <span v-if="roman" class="rank-roman">{{ roman }}</span>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import {
  leagueBadgeSrc,
  type LeagueDivision,
  type LeagueId,
} from '@shared/index'
import { useI18n } from '../i18n/t'

const props = withDefaults(
  defineProps<{
    league: LeagueId | null
    division?: LeagueDivision | null
    placementRemaining?: number
    size?: number
  }>(),
  {
    division: null,
    placementRemaining: 0,
    size: 64,
  },
)

const { t } = useI18n()

const isPlacing = computed(
  () => props.placementRemaining > 0 || !props.league,
)

const src = computed(() =>
  isPlacing.value ? null : leagueBadgeSrc(props.league),
)

const roman = computed(() => {
  if (isPlacing.value || !props.division || props.league === 'king') return ''
  return props.division === 1 ? 'I' : 'II'
})

const label = computed(() => {
  if (isPlacing.value) return t('rank.placement')
  if (!props.league) return t('rank.placement')
  const name = t(`rank.leagues.${props.league}`)
  if (!roman.value) return name
  return `${name} ${roman.value}`
})
</script>

<style scoped>
.rank-badge {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.rank-medal {
  display: block;
  border-radius: 50%;
  object-fit: cover;
}

.rank-empty {
  display: block;
  border-radius: 50%;
  background: radial-gradient(circle at 40% 35%, #4b5563, #111827);
  border: 2px solid #6b7280;
}

.rank-roman {
  position: absolute;
  right: -4%;
  bottom: -2%;
  min-width: 1.35em;
  padding: 0 4px;
  border-radius: 999px;
  background: #0d0d1a;
  border: 1px solid #e8c547;
  color: #fde68a;
  font-size: 0.72em;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-align: center;
  line-height: 1.4;
}

.placement .rank-roman {
  display: none;
}
</style>
